import { FC } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DataGridPro, GridPagination } from '@mui/x-data-grid-pro';
import {
  ExpandLess,
  ExpandMore,
  InsertDriveFile,
  Refresh,
} from '@mui/icons-material';
import { t } from 'i18next';

import { theme } from '@store';
import { toolbarFieldSx } from '@ui';
import { ConfirmationModal } from '@components';
import {
  ColumnDensityFilter,
  PinnedColumnMenu,
} from 'components/TablePreferences/';
import {
  TableColumnHeightMapper,
  TableHeaderHeight,
} from 'components/TablePreferences';

import {
  IActivityLogsTableEntry,
  IActivityLogsTableFilters,
  useActivityLogsTable,
} from '../hooks';

interface IActivityLogsTableProps {
  rows: IActivityLogsTableEntry[];
  filters: IActivityLogsTableFilters;
  setFilters: (filters: IActivityLogsTableFilters) => void;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  totalCount: number;
  loading: boolean;
  disabled?: boolean;
}

export const ActivityLogsTable: FC<IActivityLogsTableProps> = ({
  rows,
  filters,
  setFilters,
  onSearchChange,
  totalCount,
  loading,
  disabled = false,
}) => {
  const {
    apiRef,
    density,
    setDensity,
    columns,
    paginationModel,
    handlePaginationModelChange,
    sortModel,
    getDetailPanelContent,
    isResetModalOpen,
    setIsResetModalOpen,
    handleConfirmReset,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
  } = useActivityLogsTable({ filters, setFilters, disabled });

  const hasSearch = !!filters.search;

  if (rows.length === 0 && !loading && !hasSearch) {
    return (
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          padding: '64px 0',
          borderTop: 'none',
          height: 'calc(100vh - 400px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <InsertDriveFile
            sx={{ width: 64, height: 64, mb: 2 }}
            color='primary'
          />
          <Typography variant='h6'>
            {t('activityLog.table.noResults.title')}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t('activityLog.table.noResults.subTitle')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'auto',
      }}
    >
      <Box
        sx={{
          width: '100%',
          padding: 2,
          display: 'flex',
          bgcolor: 'white',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <OutlinedInput
          id='activityLogsSearchField'
          placeholder={t('activityLog.table.searchPlaceholder')}
          onChange={onSearchChange}
          defaultValue={filters.search ?? ''}
          size='medium'
          sx={{
            ...toolbarFieldSx,
            '& input': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          startAdornment={
            <InputAdornment sx={{ padding: 0 }} position='start'>
              <SearchIcon />
            </InputAdornment>
          }
        />
        <Box sx={{ display: 'flex' }}>
          <ColumnDensityFilter
            handleDensityChange={setDensity}
            selectedValue={density}
          />
        </Box>
      </Box>
      <DataGridPro
        apiRef={apiRef}
        onRowClick={params => apiRef.current?.toggleDetailPanel(params.id)}
        sx={{
          '--DataGrid-overlayHeight': 'calc(100vh - 460px)',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          maxHeight: 'calc(100vh - 460px)',
          borderTop: 'none',
          borderRadius: 0,
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: theme.palette.headerBackground.main,
          },
          '& .MuiDataGrid-columnHeader': {
            bgcolor: theme.palette.headerBackground.main,
          },
        }}
        rows={rows}
        columns={columns}
        rowCount={totalCount}
        loading={loading}
        pagination
        paginationMode='server'
        sortingMode='server'
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnSelector
        disableColumnReorder
        disableColumnSorting
        disableMultipleColumnsSorting
        columnHeaderHeight={TableHeaderHeight}
        getRowHeight={() => TableColumnHeightMapper[density]}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={() => 'auto'}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnWidthChange={handleColumnWidthChange}
        getRowClassName={params =>
          params.indexRelativeToCurrentPage % 2 !== 0 ? 'row-even' : ''
        }
        slots={{
          columnMenu: props => (
            <PinnedColumnMenu {...props} pinnedColumns={pinnedColumns} />
          ),
          detailPanelExpandIcon: (props: object) => <ExpandMore {...props} />,
          detailPanelCollapseIcon: (props: object) => <ExpandLess {...props} />,
          pagination: props => (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
              }}
            >
              <Button
                color='primary'
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<Refresh color='primary' />}
                onClick={() => setIsResetModalOpen(true)}
              >
                {t('activityLog.table.resetView')}
              </Button>
              <GridPagination {...props} />
            </Box>
          ),
        }}
      />
      <ConfirmationModal
        title={t('activityLog.resetViewModal.title')}
        message={t('activityLog.resetViewModal.message')}
        closeText={t('activityLog.resetViewModal.keep')}
        confirmText={t('activityLog.resetViewModal.confirm')}
        onConfirm={handleConfirmReset}
        onClose={() => setIsResetModalOpen(false)}
        isOpen={isResetModalOpen}
        disableCloseOnOutsideClick={false}
        reverseButtonOrder
      />
    </Box>
  );
};
